// @flow
import React, { Component, Fragment } from 'react';
import { Redirect, Route, Switch } from 'react-router';
import PropTypes from 'prop-types';
import { PrivateRoute } from '../../../router';
import BlackFridayBanner from '../../home/components/BlackFridayBanner/BlackFridayBanner';
import BlackWeekendBanner from '../../home/components/BlackWeekendBanner/BlackWeekendBanner';
import CyberMondayBanner from '../../home/components/CyberMondayBanner/CyberMondayBanner';
import SearchStepContainer from '../SearchStep/containers/SearchStepContainer';
import StepTracker from './StepTracker/StepTracker';
import ProductStepContainer from '../ProductStep/containers/ProductStepContainer';
import QuoteStepContainer from '../QuoteStep/containers/QuoteStepContainer';
import CheckoutStepContainer from '../CheckoutStep/containers/CheckoutStepContainer';
import CheckoutSuccessStepContainer from '../CheckoutSuccessStep/containers/CheckoutSuccessContainer';
import CheckoutAsyncSuccessContainer from '../CheckoutStep/containers/CheckoutAsyncSuccessContainer';
import CheckoutAsyncErrorContainer from '../CheckoutStep/containers/CheckoutAsyncErrorContainer';
import TopBanner from './TopBanner/TopBanner';
import AlertContainer from '../../alert/container/AlertContainer';
import routes from '../../../routes';
import Page from '../../../Page';
import BannerStrip from './BannerStrip/BannerStrip';
import NotFound from '../../error/components/404/404';
import CheckoutStepProxy from '../CheckoutStep/components/CheckoutStepProxy';
import { getChosenVariant } from '../../ab-test/utils';

import style from './NewOrder.scss';

import type { Step, QuoteType } from '../duck';
import type { Bag, BagItem, ShoppingCartItem, User } from '../../../entities';
import type { LocaleState } from '../../locale/duck';
import type { Location } from '../../../router';
import type { LocalValuesState } from '../../local-values/duck';
import type { VirtualItem } from '../../shopping-cart/api';

type Props = {
  +authenticated?: boolean;
  +authModalVisible?: boolean;
  +chosenVariants?: { [key: string]: number };
  +handleOpenAuthModal: (openLoginView?: boolean) => void;
  +locale: LocaleState;
  +location: Location;
  +localValues: LocalValuesState;
  +resetNewOrderState: () => void;
  +user?: User;
  +resetAlertState: () => void;
  +shoppingCart?: ShoppingCartItem[];
  +bag?: Bag;
  +isEditingShoppingCartItem?: boolean;
  +isFetchingCart?: boolean;
  +isFetchingBag?: boolean;
  +virtualShoppingCart: VirtualItem[];
  +virtualBag?: VirtualItem[];
};

type State = {};

export default class NewOrder extends Component<Props, State> {
  componentWillUnmount() {
    const { resetNewOrderState, resetAlertState } = this.props;
    resetNewOrderState();
    resetAlertState();
  }

  getStepFromUrl(): Step {
    const { location } = this.props;
    const splitted = location.pathname.split('/');
    if (splitted.length >= 3) {
      // NOTE: This means it enters to /
      if (!splitted[2]) {
        return 'scrap';
      }
      return (splitted[2]: any);
    }
    return 'scrap';
  }

  getQuoteType(): QuoteType {
    const { location } = this.props;
    const splitted = location.pathname.split('/');
    if (splitted.length >= 4) {
      return (splitted[3]: any);
    }
    return 'scrap';
  }

  buildTopBanner(step: Step) {
    const {
      locale, localValues, bag, shoppingCart, isFetchingCart, isFetchingBag, isEditingShoppingCartItem,
      virtualShoppingCart, virtualBag,
    } = this.props;
    if (step === 'product') {
      return (
        <TopBanner step={step}
                   localValues={localValues}
                   locale={locale}/>
      );
    } else if (step === 'quote') {
      const quoteType = this.getQuoteType();
      let total, items = [];
      if (quoteType === 'bag' && bag) {
        items = bag.items;
      } else if (quoteType === 'cart' && shoppingCart) {
        items = shoppingCart;
      } else if (quoteType === 'offline-bag' && virtualBag) {
        items = virtualBag;
      } else if (quoteType === 'offline-cart' && virtualShoppingCart) {
        items = virtualShoppingCart;
      }
      total = items.reduce((acc: number, item: BagItem | ShoppingCartItem) =>
        acc + parseFloat(item.price) * item.amount, 0);
      return (
        <TopBanner step={step}
                   quoteType={quoteType}
                   total={total}
                   localValues={localValues}
                   locale={locale}
                   isLoading={isFetchingCart || isFetchingBag || isEditingShoppingCartItem}/>
      );
    }
    return null;
  }

  render() {
    const {
      handleOpenAuthModal, authModalVisible, user, authenticated, chosenVariants,
    } = this.props;
    const step = this.getStepFromUrl();
    const chosenVariant = getChosenVariant(chosenVariants, 'mostEffectiveCheckoutStep');
    return (<Fragment>
      {/*{step === 'scrap' && <BannerStrip locale={locale}/>}*/}
      {step !== 'scrap' &&
      <section className={style.freeShippingBannerWrap}>
        {this.buildTopBanner(step)}
      </section>}
      {step !== 'scrap' && step !== 'product' && step !== 'checkout-success' && step !== 'checkout-error' &&
      <section className={style.alertWrap}>
        <AlertContainer showId={['new-order', 'product-helper', 'shopping-cart', 'bag', 'checkout']}/>
      </section>}
      {(/*step === 'product' || */ step === 'quote' /*|| (step === 'checkout')*/) &&
      <StepTracker step={step}/>}
      <Switch>
        <Route exact path={routes.NEW_ORDER.routePath}
               render={props => <Redirect to="/"/>}/>
        <Route exact path={routes.NEW_ORDER.children.SEARCH.routePath}
               render={props => <Page name={routes.NEW_ORDER.children.SEARCH.name}>
                 <SearchStepContainer {...props}/>
               </Page>}/>
        <Route exact path={routes.NEW_ORDER.children.PRODUCT.routePath}
               render={props => <Page name={routes.NEW_ORDER.children.PRODUCT.name}>
                 <ProductStepContainer {...props}
                                       handleOpenAuthModal={handleOpenAuthModal}
                                       authModalVisible={authModalVisible}/>
               </Page>}/>
        <PrivateRoute exact path={routes.NEW_ORDER.children.QUOTE_CART.routePath}
                      name={routes.NEW_ORDER.children.QUOTE_CART.name}
                      component={QuoteStepContainer}
                      user={user} authenticated={authenticated}
                      componentProps={{ handleOpenAuthModal, authModalVisible }}/>
        <Route exact path={routes.NEW_ORDER.children.QUOTE_OFFLINE_CART.routePath}
               render={props => <Page name={routes.NEW_ORDER.children.QUOTE_OFFLINE_CART.name}>
                 <QuoteStepContainer {...props}
                                     handleOpenAuthModal={handleOpenAuthModal}
                                     authModalVisible={authModalVisible}/>
               </Page>}/>
        <Route exact path={routes.NEW_ORDER.children.QUOTE_BAG.routePath}
               render={props => <Page name={routes.NEW_ORDER.children.QUOTE_BAG.name}>
                 <QuoteStepContainer {...props}
                                     handleOpenAuthModal={handleOpenAuthModal}
                                     authModalVisible={authModalVisible}/>
               </Page>}/>
        <Route exact path={routes.NEW_ORDER.children.QUOTE_OFFLINE_BAG.routePath}
               render={props => <Page name={routes.NEW_ORDER.children.QUOTE_OFFLINE_BAG.name}>
                 <QuoteStepContainer {...props}
                                     handleOpenAuthModal={handleOpenAuthModal}
                                     authModalVisible={authModalVisible}/>
               </Page>}/>
        <PrivateRoute exact path={routes.NEW_ORDER.children.CHECKOUT_ORDER.routePath}
                      name={routes.NEW_ORDER.children.CHECKOUT_ORDER.name}
                      component={CheckoutStepProxy}
                      user={user} authenticated={authenticated}/>
        <PrivateRoute exact path={routes.NEW_ORDER.children.CHECKOUT_CART.routePath}
                      name={routes.NEW_ORDER.children.CHECKOUT_CART.name}
                      component={CheckoutStepProxy}
                      user={user} authenticated={authenticated}/>
        <PrivateRoute exact path={routes.NEW_ORDER.children.CHECKOUT_BAG.routePath}
                      name={routes.NEW_ORDER.children.CHECKOUT_BAG.name}
                      component={CheckoutStepProxy}
                      user={user} authenticated={authenticated}/>
        <PrivateRoute exact path={routes.NEW_ORDER.children.CHECKOUT_ITEMS.routePath}
                      name={routes.NEW_ORDER.children.CHECKOUT_ITEMS.name}
                      component={CheckoutStepProxy}
                      user={user} authenticated={authenticated}/>
        <PrivateRoute exact path={routes.NEW_ORDER.children.CHECKOUT_ASYNC_ERROR.routePath}
                      name={routes.NEW_ORDER.children.CHECKOUT_ASYNC_ERROR.name}
                      component={CheckoutAsyncErrorContainer}
                      user={user} authenticated={authenticated}/>
        <PrivateRoute exact path={routes.NEW_ORDER.children.CHECKOUT_ASYNC_SUCCESS.routePath}
                      name={routes.NEW_ORDER.children.CHECKOUT_ASYNC_SUCCESS.name}
                      component={CheckoutAsyncSuccessContainer}
                      user={user} authenticated={authenticated}/>
        <PrivateRoute exact path={routes.NEW_ORDER.children.CHECKOUT_SUCCESS.routePath}
                      name={routes.NEW_ORDER.children.CHECKOUT_SUCCESS.name}
                      component={CheckoutSuccessStepContainer}
                      user={user} authenticated={authenticated}/>
        <Route render={props => <Page name={routes.NOT_FOUND.name}><NotFound {...props}/></Page>}/>
      </Switch>
    </Fragment>);
  }
};

NewOrder.propTypes = {
  authenticated: PropTypes.bool,
  authModalVisible: PropTypes.bool,
  chosenVariants: PropTypes.object,
  handleOpenAuthModal: PropTypes.func.isRequired,
  locale: PropTypes.object.isRequired,
  localValues: PropTypes.object,
  location: PropTypes.object,
  resetNewOrderState: PropTypes.func.isRequired,
  user: PropTypes.object,
  resetAlertState: PropTypes.func.isRequired,
  shoppingCart: PropTypes.array,
  bag: PropTypes.object,
  isEditingShoppingCartItem: PropTypes.bool,
  isFetchingCart: PropTypes.bool,
  isFetchingBag: PropTypes.bool,
  virtualShoppingCart: PropTypes.array.isRequired,
  virtualBag: PropTypes.array,
};



// WEBPACK FOOTER //
// ./src/modules/new-order/components/NewOrder.jsx